import React, { useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Divider,
  Avatar,
  useTheme,
  useMediaQuery,
  ListSubheader,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ForumIcon from "@mui/icons-material/Forum";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import SchoolIcon from "@mui/icons-material/School";
import EventIcon from "@mui/icons-material/EventOutlined";
import ScienceIcon from "@mui/icons-material/Science";
import TokenIcon from "@mui/icons-material/DataUsage";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import SendIcon from "@mui/icons-material/SendOutlined";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AssignmentIcon from "@mui/icons-material/AssignmentOutlined";
import ArticleIcon from "@mui/icons-material/ArticleOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import ViewAsBanner from "./ViewAsBanner";
import ImpersonationBanner from "./ImpersonationBanner";
import GenderPrompt from "../GenderPrompt";
import { UserRole } from "../../types";

const DRAWER_WIDTH = 220;

// Injected at build time from package.json (REACT_APP_VERSION=$npm_package_version
// in the start/build scripts). Lets students/staff confirm the running version.
const APP_VERSION = process.env.REACT_APP_VERSION ?? "";

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles: UserRole[];
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      {
        label: "Dashboard",
        path: "/",
        icon: <DashboardIcon />,
        roles: [
          "student",
          "admin",
          "office_manager",
          "instructor",
          "mentor",
          "parent",
        ],
      },
    ],
  },
  {
    label: "People",
    items: [
      {
        label: "Candidates",
        path: "/candidates",
        icon: <LeaderboardIcon />,
        roles: ["admin", "office_manager"],
      },
      {
        label: "1-on-1s",
        path: "/one-on-ones",
        icon: <EventIcon />,
        roles: ["admin", "office_manager", "mentor"],
      },
      {
        label: "Trials",
        path: "/trials",
        icon: <LeaderboardIcon />,
        roles: ["admin", "office_manager"],
      },
      {
        label: "Students",
        path: "/students",
        icon: <PeopleIcon />,
        roles: ["admin", "office_manager", "instructor", "mentor"],
      },
      {
        label: "Progress",
        path: "/progress",
        icon: <DashboardIcon />,
        roles: ["admin", "office_manager", "instructor", "mentor"],
      },
      {
        label: "Staff",
        path: "/staff",
        icon: <PeopleIcon />,
        roles: ["admin", "office_manager"],
      },
    ],
  },
  {
    label: "Operations",
    items: [
      {
        label: "Classes",
        path: "/classes",
        icon: <SchoolIcon />,
        roles: ["admin", "office_manager", "instructor", "mentor"],
      },
      {
        label: "Schedule",
        path: "/schedule",
        icon: <EventIcon />,
        roles: ["admin", "office_manager", "instructor", "mentor"],
      },
      {
        label: "Send Message",
        path: "/compose",
        icon: <SendIcon />,
        roles: ["admin", "office_manager", "instructor", "mentor"],
      },
      {
        label: "Fair Dates",
        path: "/fair-dates",
        icon: <EventIcon />,
        roles: ["admin", "office_manager"],
      },
    ],
  },
  {
    label: "System",
    items: [
      {
        label: "Science Fairs",
        path: "/admin/science-fairs",
        icon: <ScienceIcon />,
        roles: ["admin"],
      },
      {
        label: "Token Usage",
        path: "/admin/token-usage",
        icon: <TokenIcon />,
        roles: ["admin"],
      },
    ],
  },
  {
    items: [
      {
        label: "AI Lab",
        path: "/learn/ai-research-lab",
        icon: <PsychologyIcon />,
        roles: ["student", "admin", "office_manager", "instructor", "mentor"],
      },
      {
        /* Finished boards from past students — what Step 8 is aiming at. */
        label: "Example Boards",
        path: "/learn/boards",
        icon: <PhotoLibraryIcon />,
        roles: ["student", "admin", "office_manager", "instructor", "mentor"],
      },
      {
        label: "My Class",
        path: "/my-class",
        icon: <SchoolIcon />,
        roles: ["student"],
      },
      {
        /* Their project read as a paper: what they have built, rather than
           what to do next — which is the dashboard's job. */
        label: "Summary",
        path: "/summary",
        icon: <ArticleIcon />,
        roles: ["student"],
      },
      {
        label: "Requirements",
        path: "/requirements",
        icon: <AssignmentIcon />,
        roles: ["student"],
      },
      {
        label: "Messages",
        path: "/messages",
        icon: <ForumIcon />,
        roles: ["student"],
      },
      {
        label: "My Profile",
        path: "/profile",
        icon: <AccountCircleIcon />,
        roles: ["student", "parent"],
      },
    ],
  },
];

interface Props {
  children: React.ReactNode;
}

export default function AppShell({ children }: Props) {
  const { profile, role, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(!isMobile);

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Logo */}
      <Box sx={{ px: 2.5, py: 2.25 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 800,
            color: "#FFFFFF",
            letterSpacing: "-0.01em",
            lineHeight: 1,
          }}
        >
          Ardent
        </Typography>
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
          <Typography
            variant="caption"
            sx={{
              color: "rgba(200,151,58,1)",
              fontWeight: 600,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              fontSize: "0.6rem",
            }}
          >
            Scholar
          </Typography>
          {APP_VERSION && (
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.4)",
                fontSize: "0.6rem",
                fontWeight: 500,
              }}
            >
              v{APP_VERSION}
            </Typography>
          )}
        </Box>
      </Box>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
      <List sx={{ flex: 1, pt: 1, px: 1 }} disablePadding>
        {NAV_SECTIONS.map((section, si) => {
          const visibleItems = section.items.filter(
            (item) => role && item.roles.includes(role),
          );
          if (visibleItems.length === 0) return null;
          return (
            <React.Fragment key={si}>
              {si > 0 && (
                <Divider
                  sx={{ my: 1, borderColor: "rgba(255,255,255,0.08)" }}
                />
              )}
              {section.label && (
                <ListSubheader
                  sx={{
                    bgcolor: "transparent",
                    color: "rgba(255,255,255,0.35)",
                    fontSize: "0.6rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    lineHeight: 2,
                    px: 1.5,
                    pb: 0.25,
                  }}
                >
                  {section.label}
                </ListSubheader>
              )}
              {visibleItems.map((item) => (
                <ListItemButton
                  key={item.path}
                  selected={
                    location.pathname === item.path ||
                    (item.path !== "/" &&
                      location.pathname.startsWith(item.path))
                  }
                  onClick={() => {
                    navigate(item.path);
                    if (isMobile) setOpen(false);
                  }}
                  sx={{ borderRadius: 1, mb: 0.25 }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    slotProps={{
                      primary: { style: { fontSize: 14, fontWeight: 500 } },
                    }}
                  />
                </ListItemButton>
              ))}
            </React.Fragment>
          );
        })}
      </List>
      <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />
      <Box sx={{ p: 1.5, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
          sx={{
            width: 32,
            height: 32,
            fontSize: 13,
            bgcolor: "rgba(200,151,58,0.8)",
          }}
        >
          {profile?.first_name?.[0]}
          {profile?.last_name?.[0]}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#FFFFFF" }}
            noWrap
          >
            {profile?.first_name} {profile?.last_name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.5)", textTransform: "capitalize" }}
            noWrap
          >
            {role === "office_manager" ? "Manager" : role?.replace("_", " ")}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={signOut}
          title="Sign out"
          sx={{
            color: "rgba(255,255,255,0.5)",
            "&:hover": { color: "#FFFFFF" },
          }}
        >
          <LogoutIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <GenderPrompt />
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{ zIndex: theme.zIndex.drawer + 1 }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              onClick={() => setOpen((o) => !o)}
              edge="start"
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Ardent Scholar
            </Typography>
            {APP_VERSION && (
              <Typography variant="caption" sx={{ ml: 1, opacity: 0.6 }}>
                v{APP_VERSION}
              </Typography>
            )}
          </Toolbar>
        </AppBar>
      )}

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? open : true}
        onClose={() => setOpen(false)}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: DRAWER_WIDTH,
            boxSizing: "border-box",
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flex: 1,
          mt: isMobile ? 8 : 0,
          bgcolor: "background.default",
          minHeight: "100vh",
        }}
      >
        <ImpersonationBanner />
        <ViewAsBanner />
        {children}
      </Box>
    </Box>
  );
}
